import Box from '@mui/material/Box';
import React from 'react';

import { colors, shadows } from '../KestrixDesignSystem.jsx';

/**
 * KestrixCloseButton - Reusable close button component with multiple variants
 * 
 * @param {Object} props - Component props
 * @param {string} props.variant - Button variant ('modal' | 'note' | 'overlay')
 * @param {Function} props.onClick - Click handler function
 * @param {string} [props.size] - Button size ('small' | 'medium' | 'large')
 * @param {Object} [props.sx] - Additional styles
 * @param {boolean} [props.disabled] - Whether the button is disabled
 * @param {string} [props.position] - Position for absolutely positioned buttons ('top-right' | 'top-left' | 'bottom-right' | 'bottom-left')
 * @returns {JSX.Element} Close button component
 */
const KestrixCloseButton = ({ 
  variant = 'modal',
  onClick, 
  size = 'medium',
  sx = {}, 
  disabled = false,
  position,
  'data-testid': dataTestId,
  ...props 
}) => {
  // Size configurations
  const sizeConfig = {
    small: {
      width: '20px',
      height: '20px',
      iconSize: '14px'
    },
    medium: {
      width: '32px',
      height: '32px',
      iconSize: '16px'
    },
    large: {
      width: '40px',
      height: '40px',
      iconSize: '20px'
    }
  };

  // Variant configurations
  const variantConfig = {
    modal: {
      background: '#2b2829',
      hoverBackground: '#1a1a1a',
      color: 'white',
      shadow: shadows.consistent,
      hoverShadow: shadows.consistent,
      hoverTransform: 'translateY(-1px)'
    },
    note: {
      background: 'transparent',
      hoverBackground: 'rgba(255, 255, 255, 0.1)',
      color: colors.base.KestrixWhite,
      shadow: 'none',
      hoverShadow: 'none',
      hoverTransform: 'scale(1.1)'
    },
    overlay: {
      background: colors.base.KestrixBlack,
      hoverBackground: colors.neutral[800],
      color: colors.base.KestrixWhite,
      shadow: shadows.light,
      hoverShadow: shadows.medium,
      hoverTransform: 'scale(1.05)'
    }
  };

  const currentSize = sizeConfig[size];
  const currentVariant = variantConfig[variant];

  // Position styles for absolutely positioned buttons
  const getPositionStyles = () => {
    if (!position) return {};
    
    const baseStyles = {
      position: 'absolute',
      zIndex: 100000
    };

    switch (position) {
      case 'top-right':
        return { ...baseStyles, top: 16, right: 16 };
      case 'top-left':
        return { ...baseStyles, top: 16, left: 16 };
      case 'bottom-right':
        return { ...baseStyles, bottom: 16, right: 16 };
      case 'bottom-left':
        return { ...baseStyles, bottom: 16, left: 16 };
      default:
        return {};
    }
  };

  const handleMouseEnter = (e) => {
    if (disabled) return;
    e.target.style.background = currentVariant.hoverBackground;
    e.target.style.transform = currentVariant.hoverTransform;
    e.target.style.boxShadow = currentVariant.hoverShadow;
  };

  const handleMouseLeave = (e) => {
    if (disabled) return;
    e.target.style.background = currentVariant.background;
    e.target.style.transform = 'translateY(0) scale(1)';
    e.target.style.boxShadow = currentVariant.shadow;
  };

  const buttonStyle = {
    background: currentVariant.background,
    color: currentVariant.color,
    border: 'none',
    borderRadius: '50%',
    width: currentSize.width,
    height: currentSize.height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: currentVariant.shadow,
    transition: 'all 0.2s ease',
    opacity: disabled ? 0.5 : 1,
    ...getPositionStyles(),
    ...sx
  };

  return (
    <Box
      component="button"
      onClick={onClick}
      disabled={disabled}
      style={buttonStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={dataTestId}
      {...props}
    >
      <svg 
        width={currentSize.iconSize} 
        height={currentSize.iconSize} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
      >
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </Box>
  );
};

export default KestrixCloseButton;
